<ng-container
  *transloco="
    let t;
    scope: 'featureSearch';
    prefix: 'featureSearch.searchResults'
  "
>
  <p-table
    #resultsTable
    [value]="items()"
    dataKey="id"
    [rowHover]="true"
    [styleClass]="tableStyleClass()"
    [tableStyle]="sizing.tableStyle()"
    [scrollable]="scrollable()"
    scrollHeight="flex"
    [resizableColumns]="true"
    columnResizeMode="expand"
    (onColResize)="sizing.onColumnResize($event)"
  >
    <ng-template pTemplate="colgroup">
      <colgroup>
        @if (showSelectionColumn()) {
          <col
            class="bd-col-select"
            [style.width]="sizing.selectionColumnWidth"
          />
        }
        @for (column of columns(); track column.id) {
          <col
            [class]="column.className"
            [style.width]="sizing.columnWidth(column)"
          />
        }
        <col class="bd-col-actions" [style.width]="sizing.actionsColumnWidth" />
      </colgroup>
    </ng-template>

    @if (showHeader()) {
      <ng-template pTemplate="header">
        <tr>
          @if (showSelectionColumn()) {
            <th
              scope="col"
              class="bd-col-select"
              data-id="search-item-select-all-header"
              [class.bd-sticky-column]="isSelectionColumnPinned()"
              [class.bd-sticky-selection-column]="isSelectionColumnPinned()"
              [style.left]="isSelectionColumnPinned() ? '0px' : null"
            >
              <p-checkbox
                data-id="search-item-select-all-checkbox"
                [binary]="true"
                [ariaLabel]="t('selectAllObjects')"
                [ngModel]="selectAllChecked()"
                [indeterminate]="selectAllIndeterminate()"
                [disabled]="!selectableItems().length"
                checkboxIcon="ui-check"
                (ngModelChange)="onSelectAllChange($event)"
              ></p-checkbox>
            </th>
          }
          @for (column of columns(); track column.id) {
            @let headerMenuItems =
              columnMenuItemsById().get(column.id) ?? []
                | translateMenuItems
                | async;
            @let columnLabel =
              column.kind === 'system' ? t(column.headerKey) : column.label;
            <th
              scope="col"
              [class]="column.className"
              [class.bd-sticky-column]="isColumnPinned(column)"
              [attr.data-id]="'search-item-' + column.id + '-header'"
              pResizableColumn
              [style.left]="sizing.stickyLeft(column)"
            >
              <div class="bd-header-content">
                <span class="bd-header-label">{{ columnLabel }}</span>
                @if (headerMenuItems) {
                  <p-button
                    [attr.data-id]="
                      'search-item-' + column.id + '-header-menu-button'
                    "
                    [attr.aria-label]="t('columnActionsBtnInfo')"
                    [text]="true"
                    [pTooltip]="t('columnActionsBtnInfo')"
                    tooltipPosition="top"
                    icon="ui-ellipsis-vertical"
                    styleClass="bd-neutral bd-header-menu-button"
                    (onClick)="onMoreActionsClick($event, headerActionMenu)"
                  ></p-button>
                  <p-tiered-menu
                    #headerActionMenu
                    styleClass="bd-action-menu bd-toolbar-menu bd-more-menu bd-small"
                    appendTo="body"
                    [popup]="true"
                    [autoDisplay]="false"
                    [model]="headerMenuItems"
                  >
                    <ng-template
                      pTemplate="item"
                      let-menuItem
                      let-hasSubmenu="hasSubmenu"
                    >
                      <bd-menu-item
                        [item]="menuItem"
                        [hasSubmenu]="hasSubmenu"
                        (itemClick)="onMenuItemClick($event.event, menuItem)"
                      />
                    </ng-template>
                  </p-tiered-menu>
                }
              </div>
            </th>
          }
          <th
            scope="col"
            class="bd-col-actions"
            data-id="search-item-actions-header"
            [class.bd-sticky-column]="isActionsColumnPinned()"
            [class.bd-sticky-right-column]="isActionsColumnPinned()"
            [style.right]="isActionsColumnPinned() ? '0px' : null"
          >
            <span class="sr-only">{{ t('moreActionsBtnInfo') }}</span>
          </th>
        </tr>
      </ng-template>
    }

    <ng-template pTemplate="body" let-item>
      @let permittedMenuItems =
        (menuItemsById().get(item.id) ?? []
          | applyMenuItemPermissions
          | async) ?? [];
      @let menuItems = permittedMenuItems | translateMenuItems | async;
      @let tooltipKey = itemTooltipKey(item);
      <tr
        [attr.data-id]="'search-item-' + item.id"
        class="bd-search-item-row"
        [ngClass]="{
          'bd-search-item-selected': item.selected,
          'bd-search-item-active': isRowActive(item),
          'bd-search-item-disabled': item.disabled,
          'bd-search-item-deleted': item.deleted,
        }"
        [attr.aria-disabled]="isRowFocusable(item) ? null : 'true'"
        [attr.tabindex]="isRowFocusable(item) ? 0 : -1"
        [pTooltip]="tooltipKey ? t(tooltipKey) : ''"
        tooltipPosition="top"
        tooltipStyleClass="bd-search-result-tooltip"
        (click)="onRowClick($event, item)"
        (dblclick)="onRowDoubleClick($event, item)"
        (keydown.enter)="onRowKeyDown($event, item)"
      >
        @if (showSelectionColumn()) {
          <td
            class="bd-col-select"
            [class.bd-sticky-column]="isSelectionColumnPinned()"
            [class.bd-sticky-selection-column]="isSelectionColumnPinned()"
            [style.left]="isSelectionColumnPinned() ? '0px' : null"
          >
            <p-checkbox
              [attr.data-id]="'search-item-checkbox-' + item.id"
              [ariaLabel]="t('itemCheckboxAriaLabel', { itemName: item.name })"
              [value]="item"
              [ngModel]="selectedItems()"
              (click)="onCheckboxClick($event, item.id)"
              (keydown.enter)="onCheckboxClick($event, item.id)"
              (ngModelChange)="onSelectedItemsChange($event)"
              [disabled]="item.disabled || item.deleted"
              checkboxIcon="ui-check"
            ></p-checkbox>
          </td>
        }
        @for (column of columns(); track column.id) {
          <td
            [class]="column.className"
            [class.bd-sticky-column]="isColumnPinned(column)"
            [style.left]="sizing.stickyLeft(column)"
          >
            @if (column.kind === 'attribute') {
              @let attributeValue =
                attributeValuesByItemId().get(item.id)?.get(column.id);
              @if (attributeValue) {
                <div
                  [attr.data-id]="'search-item-' + column.id + '-' + item.id"
                  class="bd-search-item-detail bd-caption"
                >
                  {{ attributeValue | attributeValueFormat }}
                </div>
              }
            } @else {
              @switch (column.id) {
                @case ('type') {
                  <div
                    [attr.data-id]="'search-item-type-' + item.id"
                    class="bd-search-item-icon-wrapper"
                    [pTooltip]="item.type ?? ''"
                    [tooltipDisabled]="!item.type"
                    tooltipPosition="top"
                    tooltipStyleClass="bd-search-result-tooltip"
                  >
                    <span
                      [attr.data-id]="'search-item-icon-' + item.id"
                      class="bd-search-item-icon {{ item.icon }}"
                      [class.bd-search-item-icon-deleted]="item.deleted"
                    ></span>
                    <span class="sr-only">{{ item.type }}</span>
                  </div>
                }
                @case ('title') {
                  <div
                    [attr.data-id]="'search-item-title-' + item.id"
                    class="bd-search-item-title bd-body-regular"
                  >
                    {{ item.name }}
                  </div>
                }
                @case ('description') {
                  @if (item.description) {
                    <div
                      [attr.data-id]="'search-item-description-' + item.id"
                      class="bd-search-item-description bd-caption"
                    >
                      {{ item.description }}
                    </div>
                  }
                }
                @case ('source') {
                  <div class="bd-search-item-detail-chip">
                    <div class="bd-chip-content">
                      <i
                        [attr.data-id]="'item-source-icon-' + item.id"
                        class="bd-chip-icon ui-repository"
                      ></i>
                      <div
                        [attr.data-id]="'item-source-name-' + item.id"
                        class="bd-chip-text bd-label-small-gray"
                        [ngClass]="{ 'bd-disabled': item.disabled }"
                      >
                        {{
                          item.deleted
                            ? t('deleted')
                            : item.externalSource || t('unify')
                        }}
                      </div>
                    </div>
                  </div>
                }
                @case ('used-in') {
                  @if (item.usedInViews?.[0]; as firstUsedInView) {
                    <div class="bd-search-item-detail-chip">
                      <a
                        [attr.data-id]="'search-item-used-in-view-' + item.id"
                        [pTooltip]="t('usedInViewTooltip')"
                        tooltipPosition="top"
                        [routerLink]="'/views/' + firstUsedInView.id"
                        target="_blank"
                        class="bd-chip-content bd-used-in-chip"
                        (click)="$event.stopPropagation()"
                        (keydown)="$event.stopPropagation()"
                      >
                        <i
                          [attr.data-id]="'view-link-icon-' + item.id"
                          class="bd-chip-icon ui-new-tab"
                        ></i>
                        <div
                          [attr.data-id]="'view-link-name-' + item.id"
                          class="bd-chip-text bd-label-small-gray"
                        >
                          {{ firstUsedInView.name }}
                        </div>
                      </a>
                      @if (item.usedInViews && item.usedInViews.length > 1) {
                        <p-button
                          [pTooltip]="
                            item.disabled || item.deleted
                              ? ''
                              : t('moreViewsTooltip')
                          "
                          tooltipPosition="top"
                          styleClass="bd-search-item-more-views-tag"
                          outlined="false"
                          [disabled]="item.disabled || item.deleted"
                          (onClick)="onMoreViewsClick($event, item)"
                        >
                          <span
                            [attr.data-id]="'more-views-' + item.id"
                            class="bd-label-small-gray"
                            >+{{ item.usedInViews.length - 1 }}</span
                          >
                        </p-button>
                      }
                    </div>
                  }
                }
                @case ('last-modified') {
                  @if (item.systemAttributes?.updatedAt) {
                    <div
                      [attr.data-id]="'search-item-modified-' + item.id"
                      class="bd-search-item-detail bd-caption"
                    >
                      {{
                        t('modifiedAgo', {
                          timeAgo:
                            item.systemAttributes?.updatedAt | timeAgo | async,
                        })
                      }}
                    </div>
                  }
                }
              }
            }
          </td>
        }

        <td
          class="bd-col-actions"
          [class.bd-sticky-column]="isActionsColumnPinned()"
          [class.bd-sticky-right-column]="isActionsColumnPinned()"
          [style.right]="isActionsColumnPinned() ? '0px' : null"
        >
          @if (menuItems) {
            <p-button
              [attr.data-id]="'search-item-more-actions-' + item.id"
              [attr.aria-label]="t('moreActionsBtnInfo')"
              [text]="true"
              [pTooltip]="item.deleted ? '' : t('moreActionsBtnInfo')"
              tooltipPosition="top"
              icon="ui-ellipsis-vertical"
              styleClass="bd-neutral {{
                actionMenu.visible ? 'bd-selected' : ''
              }}"
              (onClick)="onMoreActionsClick($event, actionMenu)"
              [tooltipDisabled]="item.deleted"
              [disabled]="item.deleted || (item.disabled && isSelectionMode())"
            ></p-button>
            <p-tiered-menu
              #actionMenu
              styleClass="bd-action-menu bd-toolbar-menu bd-more-menu bd-small"
              appendTo="body"
              [popup]="true"
              [autoDisplay]="false"
              [model]="menuItems"
            >
              <ng-template
                pTemplate="item"
                let-menuItem
                let-hasSubmenu="hasSubmenu"
              >
                <bd-menu-item
                  [item]="menuItem"
                  [hasSubmenu]="hasSubmenu"
                  (itemClick)="onMenuItemClick($event.event, menuItem)"
                />
              </ng-template>
            </p-tiered-menu>
          }
        </td>
      </tr>
    </ng-template>
  </p-table>

  @if (manageColumnsDialogVisible()) {
    <bd-manage-object-columns-dialog
      [visible]="manageColumnsDialogVisible()"
      [columns]="columns()"
      [catalogue]="columnCatalogue()"
      (confirm)="onManageColumnsConfirm($event)"
      (dismiss)="onManageColumnsDismiss()"
    ></bd-manage-object-columns-dialog>
  }
</ng-container>
